<template>
  <div class="modal fade" :id="id" tabindex="-1" role="dialog" aria-hidden="true" data-backdrop="static">
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center mb-5">
            <h3 class="fw-bold">{{ $t('modal.title') }}</h3>
            <div class="btn btn-icon btn-sm btn-active-light-primary" data-bs-dismiss="modal" aria-label="Close">
              <span class="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor"/>
                </svg>
              </span>
            </div>
          </div>

          <div v-if="isLoading" class="text-center py-10">
            <span class="spinner-border spinner-border-lg text-primary"></span>
          </div>

          <div v-else-if="inscriptionData" class="row">
            
            <div class="card shadow-sm mb-5">
              <div class="card-body">
                <h4 class="fw-bold mb-5">{{ $t('general.title') }}</h4>
                <div class="row">
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.firstname') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.firstname }}</p>
                  </div>
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.lastname') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.lastname }}</p>
                  </div>
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.email') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.email }}</p>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.phone') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.phone_code }} {{ inscriptionData.phone }}</p>
                  </div>
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.promo_code') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.code_promo || '-' }}</p>
                  </div>
                  <div class="col-md-4 mb-4">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('general.payment_method') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.payment_method || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.selectedPrograms?.length">
              <div class="card-body">
                <h4 class="fw-bold mb-4">{{ $t('programs.title') }}</h4>
                <div class="d-flex flex-column flex-wrap gap-4">
                  <span v-for="(program, index) in inscriptionData.selectedPrograms" :key="index" 
                        class="badge badge-light-primary fs-6 px-4 py-2">
                    {{ getProgramLabel(program) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.workshops?.length">
              <div class="card-body">
                <h4 class="fw-bold mb-4">{{ $t('workshops.title') }}</h4>
                <div class="d-flex flex-wrap gap-3">
                  <span v-for="(workshop, index) in inscriptionData.workshops" :key="index" 
                        class="badge badge-light-success fs-6 px-4 py-2">
                    {{ workshop }}
                  </span>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.workshopParticipants?.length">
              <div class="card-body">
                <h4 class="fw-bold mb-4">
                  {{ $t('workshop_participants.title') }} ({{ inscriptionData.workshopParticipantCount }})
                </h4>

                <div class="table-responsive">
                  <table class="table align-middle table-row-bordered fs-6 gy-3">
                    <thead>
                      <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                        <th>{{ $t('workshop_participants.firstname') }}</th>
                        <th>{{ $t('workshop_participants.lastname') }}</th>
                        <th>{{ $t('workshop_participants.email') }}</th>
                        <th>{{ $t('workshop_participants.phone') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(participant, index) in inscriptionData.workshopParticipants" :key="index">
                        <td>{{ participant.firstname }}</td>
                        <td>{{ participant.lastname }}</td>
                        <td>{{ participant.email }}</td>
                        <td>{{ participant.phone_code }} {{ participant.phone }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.bookCeremony?.length">
              <div class="card-body">
                <h4 class="fw-bold mb-4">{{ $t('ceremony.title') }}</h4>
                <div class="row">
                  <div class="col-md-8 mb-3">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('ceremony.selection') }}</label>
                    <div class="d-flex flex-wrap gap-2">
                      <span v-for="(ceremony, index) in inscriptionData.bookCeremony" :key="index" 
                            class="badge badge-light-info fs-6 px-3 py-2">
                        {{ ceremony }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('ceremony.count') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.bookCount }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.galaParticipants?.length">
              <div class="card-body">
                <h4 class="fw-bold mb-4">
                  {{ $t('gala.title') }} ({{ inscriptionData.galaParticipantCount }})
                </h4>
                <div class="table-responsive">
                  <table class="table align-middle table-row-bordered fs-6 gy-3">
                    <thead>
                      <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                        <th>{{ $t('gala.firstname') }}</th>
                        <th>{{ $t('gala.lastname') }}</th>
                        <th>{{ $t('gala.email') }}</th>
                        <th>{{ $t('gala.phone') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(participant, index) in inscriptionData.galaParticipants" :key="index">
                        <td>{{ participant.firstname }}</td>
                        <td>{{ participant.lastname }}</td>
                        <td>{{ participant.email }}</td>
                        <td>{{ participant.phone_code }} {{ participant.phone }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-5" v-if="inscriptionData.coachingType">
              <div class="card-body">
                <h4 class="fw-bold mb-4">{{ $t('coaching.title') }}</h4>

                <div class="row mb-4">
                  <div class="col-md-6 mb-3">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.type') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.coachingType }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.coach') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.coach || '-' }}</p>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.participants_count') }}</label>
                    <p class="fs-6 mb-0">{{ inscriptionData.coachingParticipantCount }}</p>
                  </div>
                </div>

                <div v-if="inscriptionData.coachingRequest?.object || inscriptionData.coachingRequest?.comment">
                  <div class="separator separator-dashed my-4"></div>
                  <h5 class="fw-bold mb-3">{{ $t('coaching.request_title') }}</h5>

                  <div class="row">
                    <div class="col-12 mb-3" v-if="inscriptionData.coachingRequest.object">
                      <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.object') }}</label>
                      <p class="fs-6 mb-0">{{ inscriptionData.coachingRequest.object }}</p>
                    </div>

                    <div class="col-12 mb-3" v-if="inscriptionData.coachingRequest.comment">
                      <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.comment') }}</label>
                      <p class="fs-6 mb-0">{{ inscriptionData.coachingRequest.comment }}</p>
                    </div>
                  </div>
                </div>

                <div v-if="inscriptionData.coachingComment">
                  <div class="separator separator-dashed my-4"></div>
                  <label class="form-label fw-bold text-muted fs-7">{{ $t('coaching.general_comment') }}</label>
                  <p class="fs-6 mb-0">{{ inscriptionData.coachingComment }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="alert alert-danger">
            {{ $t('modal.no_data') }}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            {{ $t('modal.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'

const programs = [
  {key: "PROGRAM_1", value: 'LES WORKSHOPS 1 & 2  (50 000 fcfa  HT)'},
  {key: 'PROGRAM_2', value: 'CEREMONIE DE DEDICACE ET  CONFERENCE (10 000 fcfa HT)'},
  {key: 'PROGRAM_3', value: 'PARTICIPATION AU DINER GALA  (50 000 FCFA HT'},
  {key: 'PROGRAM_4', value: 'RESERVATION DE SEANCE DE COACHING RE-BRAND'},
]

const getProgramLabel = (key) => {
  const program = programs.find(p => p.key === key);
  return program ? program.value : key;
}

const store = useStore()

const props = defineProps({
  id: { type: String, required: true },
  inscriptionId: { type: String, default: null },
  eventName: { type: String, default: '' }
})

const isLoading = ref(false)
const inscriptionData = ref(null)

watch(() => props.inscriptionId, (newVal) => {
  if (newVal) loadInscriptionData()
}, { immediate: true })

function loadInscriptionData() {
  if (!props.inscriptionId) return

  isLoading.value = true
  store.dispatch('events/getSenapkonInscription', props.inscriptionId)
    .then(res => {
      inscriptionData.value = res.data.inscription
    })
    .catch(err => {
      console.error('Erreur lors du chargement:', err)
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>

<style scoped>
@media (min-width: 1200px) {
  .modal-xl {
    max-width: 1400px;
  }
}

.separator {
  height: 1px;
  background-color: #e4e6ef;
}
</style>
